import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { firestore } from '../firebase/Init';
import { useAuth } from '../contexts/AuthContext';
import {
  isPro as isProDoc,
  hasFeature as hasFeatureDoc,
  trialDaysLeft as trialDaysLeftDoc,
} from '../contracts/billingApi';

// The single entitlement subscription. Every entitlement-aware surface reads
// through this hook — nothing else touches the collection.
//
// LIVE, NOT FETCHED. It is an onSnapshot rather than a one-shot read because
// of one specific moment: the user returns from Stripe Checkout and the
// webhook lands a second or two later. With a fetch, the membership they just
// paid for appears only after a manual refresh. With a subscription, the
// paywall dissolves on its own.
//
// FAIL-OPEN IS NOT AN OPTION HERE, BUT NEITHER IS FAIL-CLOSED-AND-SILENT.
// A read error leaves `entitlement` null, so hasFeature() is false and the
// paywall shows. That is the safe direction, but it can show a paywall to a
// paying member during an outage — so `error` is exposed separately and the
// gate renders a "temporarily unavailable" state rather than a sales pitch.
// The server gate (utilities/requireEntitlement.js) makes the same distinction.
//
// This hook is advisory. It drives what the UI SHOWS. It is not a security
// boundary — every gated payload is withheld server-side, and a user editing
// their own client state gains nothing but a broken-looking screen.
export default function useEntitlement() {
  // `|| {}` matches the `useUser() || {}` idiom already used across the pages:
  // the hook is called from surfaces that render outside an AuthProvider in
  // tests and in the signed-out shell, and a missing provider should read as
  // "signed out", not throw.
  const { currentUser } = useAuth() || {};
  const uid = currentUser?.uid || null;

  const [state, setState] = useState({
    entitlement: null,
    isLoading: Boolean(uid),
    error: null,
  });

  // Guards a setState after unmount when the snapshot fires late.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!uid) {
      setState({ entitlement: null, isLoading: false, error: null });
      return undefined;
    }

    setState((s) => ({ ...s, isLoading: true, error: null }));

    const unsubscribe = onSnapshot(
      doc(firestore, 'entitlements', uid),
      (snap) => {
        if (!mountedRef.current) return;
        // A missing doc is the ordinary free-tier state, not an error.
        setState({
          entitlement: snap.exists() ? snap.data() : null,
          isLoading: false,
          error: null,
        });
      },
      (err) => {
        if (!mountedRef.current) return;
        console.error('useEntitlement: snapshot failed', err);
        setState({ entitlement: null, isLoading: false, error: err });
      },
    );

    return unsubscribe;
  }, [uid]);

  const { entitlement, isLoading, error } = state;

  const hasFeature = useCallback(
    (feature) => hasFeatureDoc(entitlement, feature),
    [entitlement],
  );

  return useMemo(() => ({
    entitlement,
    isLoading,
    error,
    // Never compare `status` at a call site — billingApi owns that, so that
    // adding a status later is a one-file change rather than a hunt.
    isPro: isProDoc(entitlement),
    hasFeature,
    trialDaysLeft: trialDaysLeftDoc(entitlement),
  }), [entitlement, isLoading, error, hasFeature]);
}
